import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { AppShell } from "../components/app-shell.js";
import { useConfirm } from "../features/canvas-kit/ui/confirm-dialog.jsx";
import { NoteEditor } from "../features/notes/editor.jsx";
import { type NoteSnapshot, parseNoteSnapshot } from "../features/notes/snapshot.js";
import { useSync } from "../providers/sync-provider";
import type { BoardRow } from "../repos/boards.js";
import { deleteNote, listNotes, updateNote } from "../repos/notes.js";

/**
 * Note editor route — `/whiteboard/note/:noteId`.
 *
 * Sibling to the whiteboard editor route. Notes live under `/whiteboard/*`
 * because the overview-grid is shared (see `whiteboard.tsx`), but the
 * editor surface is entirely different: a TipTap text editor in a single
 * comfortable content column.
 */
export function NoteEditorPage() {
  const { noteId } = useParams<{ noteId: string }>();
  const navigate = useNavigate();
  const { db, orgId, userId, engine } = useSync();
  const qc = useQueryClient();
  const ready = Boolean(db && orgId && userId);

  const notes = useQuery<BoardRow[]>({
    queryKey: ["notes", orgId, userId],
    enabled: ready,
    queryFn: () => listNotes(db!, orgId!, userId!),
    refetchInterval: 5000,
  });

  const active = useMemo(
    () => notes.data?.find((n) => n.id === noteId) ?? null,
    [notes.data, noteId],
  );

  useEffect(() => {
    if (!ready) return;
    if (notes.isLoading) return;
    if (!active && noteId) navigate("/whiteboard", { replace: true });
  }, [ready, notes.isLoading, active, noteId, navigate]);

  const save = useMutation({
    mutationFn: async (args: { id: string; name: string; snapshot: NoteSnapshot }) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await updateNote(db!, {
        id: args.id,
        name: args.name,
        snapshot: args.snapshot,
      });
      void engine?.syncNow();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes"], exact: false });
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      if (!ready) throw new Error("Lokale Datenbank nicht bereit.");
      await deleteNote(db!, { id, orgId: orgId! });
      void engine?.syncNow();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notes"], exact: false });
      navigate("/whiteboard");
    },
  });

  const { confirm, dialog } = useConfirm();

  const snap = useMemo(
    () => (active ? parseNoteSnapshot(active.snapshot_json) : null),
    // Re-parse only when the underlying JSON actually changes, otherwise
    // periodic refetches with identical content would wipe unsaved edits.
    [active?.snapshot_json],
  );

  if (!active || !snap) {
    return (
      <AppShell title="Notiz" wide chromeless>
        <div className="flex h-[60vh] items-center justify-center text-body-sm text-muted-foreground">
          {notes.isLoading ? "Lade Notiz…" : "Notiz nicht gefunden."}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Notiz" wide chromeless>
      <NoteEditor
        key={active.id}
        name={active.name}
        snapshot={snap}
        saving={save.isPending}
        onClose={() => navigate("/whiteboard")}
        onSave={({ name, snapshot }) => save.mutate({ id: active.id, name, snapshot })}
        onDelete={async () => {
          const ok = await confirm({
            title: "Notiz löschen?",
            message: `„${active.name}“ wird unwiderruflich gelöscht.`,
            confirmLabel: "Löschen",
          });
          if (ok) remove.mutate(active.id);
        }}
      />
      {dialog}
    </AppShell>
  );
}
